import { getLoggr } from '@opcentral/loggr-lib';
import type {
  CaptureOptions,
  Idea,
  ExpandOptions,
  Draft,
  DraftSection,
  SearchOptions,
  ExpressrConfig,
  LLMProvider,
  SynthesisOptions,
  SynthesisResult,
  EditrExport,
} from './types.js';
import { v4 as uuidv4 } from 'uuid';

export class Expressr {
  private logger = getLoggr({ processName: 'Expressr' });
  private ideas: Map<string, Idea> = new Map();
  private drafts: Map<string, Draft> = new Map();
  private llm?: LLMProvider;

  constructor(config?: ExpressrConfig) {
    if (config?.llm) {
      this.llm = config.llm;
      this.logger.info('Expressr initialized with LLM provider');
    }
  }

  /**
   * Capture a quick idea
   */
  async capture(options: CaptureOptions): Promise<Idea> {
    this.logger.info(`Capturing ${options.type} idea`);

    const idea: Idea = {
      id: uuidv4(),
      type: options.type,
      content: options.content,
      tags: options.tags || [],
      relatedIdeas: [],
      metadata: options.metadata || {},
      createdAt: new Date(),
      updatedAt: new Date()
    };

    this.ideas.set(idea.id, idea);
    this.logger.info(`Idea captured: ${idea.id}`);

    return idea;
  }

  /**
   * Expand an idea into a draft
   */
  async expand(ideaId: string, options: ExpandOptions): Promise<Draft> {
    const idea = this.ideas.get(ideaId);
    if (!idea) {
      throw new Error(`Idea not found: ${ideaId}`);
    }

    this.logger.info(`Expanding idea: ${ideaId}`);

    const sections: DraftSection[] = [];
    
    if (options.sections) {
      options.sections.forEach((title, index) => {
        sections.push({
          id: uuidv4(),
          title,
          content: '',
          order: index
        });
      });
    } else {
      // Default section with idea content
      sections.push({
        id: uuidv4(),
        title: 'Main Content',
        content: idea.content,
        order: 0
      });
    }

    const draft: Draft = {
      id: uuidv4(),
      ideaId,
      format: options.format,
      content: this.formatDraft(sections, options.format),
      template: options.template,
      sections,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    this.drafts.set(draft.id, draft);
    this.logger.info(`Draft created: ${draft.id}`);

    return draft;
  }

  /**
   * Synthesize content from sources using LLM.
   * This is the core "creative writing" capability.
   *
   * Flow: expressr coordinates → assistr provides LLM → charactr provides audience adaptation
   * The LLM provider is injected via constructor (keeps expressr free of AI SDK deps).
   *
   * @example
   * ```typescript
   * // Wire assistr as the LLM provider
   * const expressr = new Expressr({
   *   llm: {
   *     generate: (prompt, opts) => assistr.ask({ message: prompt, ...opts })
   *       .then(r => ({ content: r.content, tokensUsed: r.tokensUsed, cost: r.cost }))
   *   }
   * });
   *
   * const result = await expressr.synthesize({
   *   topic: 'Flourish People Empowerment Ecosystem',
   *   sourceContent: [phoenixSearchResults...],
   *   tone: 'persuasive',
   *   audience: 'investor',
   *   format: 'markdown',
   *   length: 'comprehensive',
   * });
   * ```
   */
  async synthesize(options: SynthesisOptions): Promise<SynthesisResult> {
    if (!this.llm) {
      throw new Error(
        'No LLM provider configured. Pass an LLM provider via new Expressr({ llm: ... }). ' +
        'Use assistr-lib\'s AssistantManager or any compatible { generate() } interface.'
      );
    }

    this.logger.info(`Synthesizing content for topic: ${options.topic}`);

    const tone = options.tone || 'professional';
    const audience = options.audience || 'general';
    const format = options.format || 'markdown';
    const length = options.length || 'standard';

    // Build the synthesis prompt
    const prompt = this.buildSynthesisPrompt(options, tone, audience, format, length);

    // Temperature mapping: creative tones get higher temperature
    const temperatureMap: Record<string, number> = {
      professional: 0.4,
      casual: 0.6,
      persuasive: 0.5,
      educational: 0.4,
      creative: 0.8,
      philosophical: 0.7,
    };
    const temperature = options.temperature ?? temperatureMap[tone] ?? 0.5;

    // Length → maxTokens mapping
    const maxTokensMap: Record<string, number> = {
      brief: 1000,
      standard: 3000,
      comprehensive: 8000,
    };
    const maxTokens = maxTokensMap[length] ?? 3000;

    this.logger.info(`Calling LLM: tone=${tone}, audience=${audience}, temp=${temperature}, maxTokens=${maxTokens}`);

    const llmResult = await this.llm.generate(prompt, {
      temperature,
      maxTokens,
    });

    const result: SynthesisResult = {
      content: llmResult.content,
      format,
      metadata: {
        topic: options.topic,
        tone,
        audience,
        sourceCount: options.sourceContent?.length ?? 0,
        generatedAt: new Date(),
        tokensUsed: llmResult.tokensUsed,
        cost: llmResult.cost,
        provider: llmResult.provider,
      },
    };

    this.logger.info(`Synthesis complete: ${result.content.length} chars, ${llmResult.tokensUsed ?? '?'} tokens`);

    // Also capture as an idea for reference
    const idea = await this.capture({
      type: 'text',
      content: result.content,
      tags: ['synthesized', tone, audience, options.topic.toLowerCase().replace(/\s+/g, '-')],
      metadata: { synthesisOptions: options, ...result.metadata },
    });

    this.logger.info(`Synthesis saved as idea: ${idea.id}`);

    return result;
  }

  /**
   * Build the LLM prompt for synthesis.
   * Structured to give the LLM clear instructions about tone, audience, and content.
   */
  private buildSynthesisPrompt(
    options: SynthesisOptions,
    tone: string,
    audience: string,
    format: string,
    length: string,
  ): string {
    const parts: string[] = [];

    // Role instruction
    parts.push(`You are a skilled writer creating content about: ${options.topic}`);

    // Tone guidance
    const toneGuidance: Record<string, string> = {
      professional: 'Write in a professional, authoritative tone. Clear, direct, and well-structured.',
      casual: 'Write in a conversational, approachable tone. Engaging but still informative.',
      persuasive: 'Write persuasively. Build a compelling case with evidence and emotional resonance. Guide the reader toward agreement.',
      educational: 'Write to educate. Explain concepts clearly, build understanding progressively, use examples.',
      creative: 'Write creatively. Use vivid language, metaphors, and narrative techniques. Engage the imagination.',
      philosophical: 'Write with philosophical depth. Ideas should build upon each other with a sense of inevitability. Tension is subtly constructed and resolved. Guide the reader across a landscape, not through bullet points.',
    };
    parts.push(`\nTone: ${toneGuidance[tone] || toneGuidance.professional}`);

    // Audience guidance
    const audienceGuidance: Record<string, string> = {
      technical: 'The audience is technical. Use precise terminology, include implementation details, reference specific technologies.',
      executive: 'The audience is C-level executives. Focus on ROI, strategic impact, competitive advantage. Be concise and impactful.',
      general: 'The audience is general. Avoid jargon, explain concepts simply, focus on benefits and outcomes.',
      investor: 'The audience is investors. Lead with market opportunity, differentiation, and growth potential. Include metrics where available.',
      partner: 'The audience is potential partners. Emphasize mutual benefit, integration opportunities, and shared vision.',
      internal: 'The audience is the internal team. Be direct, include context they need, focus on what needs to happen and why.',
      public: 'The audience is the public. Be inspiring, accessible, and authentic. Focus on impact and human stories.',
    };
    parts.push(`\nAudience: ${audienceGuidance[audience] || audienceGuidance.general}`);

    // Length guidance
    const lengthGuidance: Record<string, string> = {
      brief: 'Keep it concise — 1-3 paragraphs maximum. Every sentence must earn its place.',
      standard: 'Standard length — enough to cover the topic thoroughly without padding. Typically 500-1500 words.',
      comprehensive: 'Comprehensive treatment — cover all aspects in depth. Include sections, examples, and supporting detail. 1500-4000 words.',
    };
    parts.push(`\nLength: ${lengthGuidance[length] || lengthGuidance.standard}`);

    // Format
    if (format === 'markdown') {
      parts.push('\nFormat: Output in Markdown. Use headers, bold, lists where appropriate.');
    } else if (format === 'html') {
      parts.push('\nFormat: Output in clean HTML. Use semantic tags (h2, p, ul, strong).');
    } else {
      parts.push('\nFormat: Output as plain text. No markup.');
    }

    // Theme
    if (options.theme) {
      parts.push(`\nTheme/Angle: ${options.theme}`);
    }

    // Sections
    if (options.sections && options.sections.length > 0) {
      parts.push(`\nStructure the output with these sections:\n${options.sections.map(s => `- ${s}`).join('\n')}`);
    }

    // Custom instructions
    if (options.instructions) {
      parts.push(`\nAdditional instructions: ${options.instructions}`);
    }

    // Source content
    if (options.sourceContent && options.sourceContent.length > 0) {
      parts.push(`\n--- SOURCE MATERIAL (synthesize from these ${options.sourceContent.length} sources) ---\n`);
      for (let i = 0; i < options.sourceContent.length; i++) {
        const source = options.sourceContent[i];
        // Truncate very long sources to keep prompt manageable
        const truncated = source!.length > 4000 ? source!.substring(0, 4000) + '\n[...truncated]' : source;
        parts.push(`[Source ${i + 1}]:\n${truncated}\n`);
      }
      parts.push('--- END SOURCE MATERIAL ---');
      parts.push('\nSynthesize the above sources into a cohesive document. Do not copy verbatim — distill, reframe, and present the information according to the tone and audience specified.');
    } else {
      parts.push('\nWrite original content based on the topic and your knowledge. Be specific and substantive.');
    }

    return parts.join('\n');
  }

  /**
   * Add tags to an idea
   */
  async addTags(ideaId: string, tags: string[]): Promise<void> {
    const idea = this.ideas.get(ideaId);
    if (!idea) {
      throw new Error(`Idea not found: ${ideaId}`);
    }

    const newTags = tags.filter(tag => !idea.tags.includes(tag));
    idea.tags.push(...newTags);
    idea.updatedAt = new Date();

    this.ideas.set(ideaId, idea);
    this.logger.info(`Added ${newTags.length} tags to idea: ${ideaId}`);
  }

  /**
   * Remove tags from an idea
   */
  async removeTags(ideaId: string, tags: string[]): Promise<void> {
    const idea = this.ideas.get(ideaId);
    if (!idea) {
      throw new Error(`Idea not found: ${ideaId}`);
    }

    idea.tags = idea.tags.filter(tag => !tags.includes(tag));
    idea.updatedAt = new Date();

    this.ideas.set(ideaId, idea);
    this.logger.info(`Removed ${tags.length} tags from idea: ${ideaId}`);
  }

  /**
   * Link related ideas
   */
  async linkIdeas(ideaId: string, relatedId: string): Promise<void> {
    const idea = this.ideas.get(ideaId);
    const relatedIdea = this.ideas.get(relatedId);

    if (!idea) {
      throw new Error(`Idea not found: ${ideaId}`);
    }
    if (!relatedIdea) {
      throw new Error(`Related idea not found: ${relatedId}`);
    }

    if (!idea.relatedIdeas.includes(relatedId)) {
      idea.relatedIdeas.push(relatedId);
      idea.updatedAt = new Date();
      this.ideas.set(ideaId, idea);
    }

    if (!relatedIdea.relatedIdeas.includes(ideaId)) {
      relatedIdea.relatedIdeas.push(ideaId);
      relatedIdea.updatedAt = new Date();
      this.ideas.set(relatedId, relatedIdea);
    }

    this.logger.info(`Linked ideas: ${ideaId} <-> ${relatedId}`);
  }

  /**
   * Search ideas by tags
   */
  async searchByTags(tags: string[]): Promise<Idea[]> {
    this.logger.info(`Searching ideas by tags: ${tags.join(', ')}`);

    const results: Idea[] = [];

    for (const idea of this.ideas.values()) {
      const hasAllTags = tags.every(tag => idea.tags.includes(tag));
      if (hasAllTags) {
        results.push(idea);
      }
    }

    this.logger.info(`Found ${results.length} ideas`);
    return results;
  }

  /**
   * Search ideas with options
   */
  async search(options: SearchOptions): Promise<Idea[]> {
    this.logger.info('Searching ideas with options');

    let results = Array.from(this.ideas.values());

    // Filter by tags
    if (options.tags && options.tags.length > 0) {
      results = results.filter(idea =>
        options.tags!.every(tag => idea.tags.includes(tag))
      );
    }

    // Filter by type
    if (options.type) {
      results = results.filter(idea => idea.type === options.type);
    }

    // Filter by date range
    if (options.dateFrom) {
      results = results.filter(idea => idea.createdAt >= options.dateFrom!);
    }
    if (options.dateTo) {
      results = results.filter(idea => idea.createdAt <= options.dateTo!);
    }

    // Apply limit
    if (options.limit) {
      results = results.slice(0, options.limit);
    }

    this.logger.info(`Found ${results.length} ideas`);
    return results;
  }

  /**
   * Export a draft to editr-ui as an EditrExport.
   * The UI layer passes this to editr-ui's MarkdownEditor as props.
   */
  async exportToEditr(draftId: string): Promise<EditrExport> {
    const draft = this.drafts.get(draftId);
    if (!draft) {
      throw new Error(`Draft not found: ${draftId}`);
    }

    const idea = this.ideas.get(draft.ideaId);

    this.logger.info(`Exporting draft to Editr: ${draftId}`);

    return {
      content: draft.content,
      format: draft.format,
      title: draft.sections[0]?.title ?? 'Untitled',
      draftId: draft.id,
      ideaId: draft.ideaId,
      tags: idea?.tags ?? [],
      sections: draft.sections,
      metadata: idea?.metadata ?? {},
      exportedAt: new Date(),
    };
  }

  /**
   * Export to Markdown
   */
  async exportToMarkdown(ideaId: string): Promise<string> {
    const idea = this.ideas.get(ideaId);
    if (!idea) {
      throw new Error(`Idea not found: ${ideaId}`);
    }

    this.logger.info(`Exporting idea to Markdown: ${ideaId}`);

    let markdown = `# Idea: ${ideaId}\n\n`;
    markdown += `**Created**: ${idea.createdAt.toISOString()}\n`;
    markdown += `**Tags**: ${idea.tags.join(', ')}\n\n`;
    markdown += `## Content\n\n${idea.content}\n`;

    if (idea.relatedIdeas.length > 0) {
      markdown += `\n## Related Ideas\n\n`;
      idea.relatedIdeas.forEach(relatedId => {
        markdown += `- ${relatedId}\n`;
      });
    }

    return markdown;
  }

  /**
   * Get an idea by ID
   */
  async getIdea(ideaId: string): Promise<Idea> {
    const idea = this.ideas.get(ideaId);
    if (!idea) {
      throw new Error(`Idea not found: ${ideaId}`);
    }
    return idea;
  }

  /**
   * Get a draft by ID
   */
  async getDraft(draftId: string): Promise<Draft> {
    const draft = this.drafts.get(draftId);
    if (!draft) {
      throw new Error(`Draft not found: ${draftId}`);
    }
    return draft;
  }

  /**
   * Helper: Format draft content
   */
  private formatDraft(sections: DraftSection[], format: 'markdown' | 'html' | 'plain'): string {
    if (format === 'markdown') {
      return sections
        .sort((a, b) => a.order - b.order)
        .map(section => `## ${section.title}\n\n${section.content}\n`)
        .join('\n');
    } else if (format === 'html') {
      return sections
        .sort((a, b) => a.order - b.order)
        .map(section => `<h2>${section.title}</h2>\n<p>${section.content}</p>`)
        .join('\n');
    } else {
      return sections
        .sort((a, b) => a.order - b.order)
        .map(section => `${section.title}\n\n${section.content}`)
        .join('\n\n');
    }
  }
}
